import { Attachment } from "@/types/attachment";

export type MediaKind = "image" | "video" | "audio" | "other";

const EXTENSION_MAP: Record<Exclude<MediaKind, "other">, Set<string>> = {
  image: new Set([
    "jpg",
    "jpeg",
    "png",
    "gif",
    "bmp",
    "webp",
    "svg",
    "tif",
    "tiff",
    "heic",
    "heif",
    "avif",
  ]),
  video: new Set([
    "mp4",
    "m4v",
    "mov",
    "avi",
    "wmv",
    "flv",
    "webm",
    "mkv",
    "3gp",
    "3g2",
  ]),
  audio: new Set([
    "mp3",
    "wav",
    "aac",
    "m4a",
    "flac",
    "ogg",
    "oga",
    "opus",
    "wma",
    "aiff",
    "aif",
  ]),
};

function inferKindFromMime(mime: string): MediaKind {
  const normalized = mime.toLowerCase();
  if (normalized.startsWith("image/")) {
    return "image";
  }
  if (normalized.startsWith("video/")) {
    return "video";
  }
  if (normalized.startsWith("audio/")) {
    return "audio";
  }
  if (normalized === "application/ogg" || normalized === "application/x-ogg") {
    return "audio";
  }
  if (normalized === "application/mp4") {
    return "video";
  }
  return "other";
}

function extractExtension(source: string): string {
  // 移除 query/hash/fragment
  const clean = source.split(/[?#]/)[0];
  const dotIndex = clean.lastIndexOf(".");
  if (dotIndex < 0) {
    return "";
  }
  return clean.slice(dotIndex + 1).toLowerCase();
}

function inferKindFromExtension(extension: string): MediaKind {
  const ext = extension.toLowerCase();
  if (EXTENSION_MAP.image.has(ext)) {
    return "image";
  }
  if (EXTENSION_MAP.video.has(ext)) {
    return "video";
  }
  if (EXTENSION_MAP.audio.has(ext)) {
    return "audio";
  }
  return "other";
}

export function isImageFile(filePath: string): boolean {
  const extension = extractExtension(filePath);
  const kind = inferKindFromExtension(extension);
  return kind === "image";
}

export function isVideoFile(filePath: string): boolean {
  const extension = extractExtension(filePath);
  const kind = inferKindFromExtension(extension);
  return kind === "video";
}

export function isAudioFile(filePath: string): boolean {
  const extension = extractExtension(filePath);
  const kind = inferKindFromExtension(extension);
  return kind === "audio";
}

/**
 * 判断文件扩展名是否为视频或音频
 */
export function isVideoOrAudioFile(filePath: string): boolean {
  return isVideoFile(filePath) || isAudioFile(filePath);
}

export function inferMediaKind(attachment: Attachment): MediaKind {
  if (attachment.type === "file") {
    return inferKindFromMime(attachment.file.type);
  } else if (attachment.type === "url") {
    return inferKindFromExtension(extractExtension(attachment.url));
  } else {
    return "other";
  }
}

/**
 * 触发文件下载
 * 注意：服务器需要设置 Content-Disposition 响应头以确保浏览器下载而非预览
 * @param url 文件 URL
 * @param filename 可选的文件名，如果不提供则从 URL 中提取
 */
export function downloadFile(url: string, filename?: string) {
  try {
    // 从 URL 猜文件名
    if (!filename) {
      try {
        const u = new URL(url);
        filename = decodeURIComponent(
          u.pathname.split("/").pop() || "download"
        );
      } catch {
        filename = "download";
      }
    }

    // 添加 download=true 查询参数
    const downloadUrl = url.includes("?")
      ? `${url}&download=true`
      : `${url}?download=true`;

    // 直接使用 <a> 标签触发下载
    // 服务器的 Content-Disposition 响应头会确保浏览器下载而不是预览
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = filename;
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } catch (error) {
    console.error("download file error:", error);
    throw error;
  }
}
export async function selectFile(accept?: string): Promise<File> {
  return new Promise((resolve, reject) => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = false;
    input.accept =
      accept || "image/jpeg,image/png,image/webp,image/jpg,video/mp4";
    input.style.display = "none";
    document.body.appendChild(input);

    const handleFileSelect = async (e: Event) => {
      try {
        const files = (e.target as HTMLInputElement).files;
        if (files && files.length > 0) {
          const f = files[0];
          resolve(f);
        } else {
          reject(new Error("No file selected"));
        }
      } catch (error) {
        reject(error);
      } finally {
        const parent = input.parentNode;
        if (parent) {
          parent.removeChild(input);
        }
      }
    };
    input.addEventListener("change", handleFileSelect);
    input.click();
  });
}

/**
 * 将 Data URL 转换为 File 对象
 */
export async function dataUrlToFile(
  dataUrl: string,
  name: string,
  type: string,
  lastModified: number
): Promise<File> {
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  return new File([blob], name, { type, lastModified });
}
